import { Component, DestroyRef, Inject, isDevMode, Optional, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { AlertService } from 'src/app/services/alert.service';
import { IProduct } from 'src/app/models/products/product';
import { OutputConfirmProductSearch } from 'src/app/models/products/productSearch';
import { ProductsService } from 'src/app/services/products.service';
import { ProductListResult } from 'src/app/models/products/product-result';
import { DownloadOutputService } from 'src/app/services/download-output.service';
import { GenerateOutputAssetsResult } from 'src/app/models/download-output/download-output-result';
import {
  EvGenerateOutputAssetsRequest,
  GenerateOutputAssetsRequest,
  RangeGenerateOutputAssetsRequest,
} from 'src/app/models/download-output/generate-output-assets-request';
import { OutputFormulaReference } from 'src/app/models/outputs-collections/shared/output-formula-reference';
import { tap } from 'rxjs';
import { DialogResult } from 'src/app/models/dialog-result';
import {
  OutputFormulaSelectionChange,
  PickedFormulaMedia,
} from 'src/app/components/shared/output-formula-picker/output-formula-picker.component';
import {
  aggregateEvCoverage,
  describeEvCoverage,
  EvConventionEntry,
} from 'src/app/components/shared/output-formula-picker/coverage-helpers';
import { FilenameConventionService } from 'src/app/services/filename-conventions/filename-convention.service';
import { OutputFormula } from 'src/app/models/output-formulas/output-formula';
import { outputFormulaValueIsEvFamily } from 'src/app/models/output-formulas/output-formula-value';
import { buildFormulaReferences } from './formula-reference-builder';
import { OutputsCollectionsService } from 'src/app/services/outputs-collections.service';
import { ReviewOutputFilenamesDialogComponent } from 'src/app/components/home/output-collections/review-output-filenames-dialog/review-output-filenames-dialog.component';
import { ReviewOutputFilenamesDialogData } from 'src/app/models/outputs-collections/review-output-filenames/dialog-data';

@Component({
  selector: 'app-create-outputs',
  templateUrl: './create-outputs.component.html',
  styleUrl: './create-outputs.component.scss',
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class CreateOutputsComponent {
  readonly DialogResult = DialogResult;
  isLoading = false;
  isDownload = false;
  postAssetIds: readonly string[] = [];
  overwrite: FormControl = new FormControl<boolean>(false);
  products: IProduct[] = [];
  selectedFormulaIds: string[] = [];
  selectedPerMediaSelection: readonly PickedFormulaMedia[] = [];
  formulaCoverageSummary = 'Coverage: no formulas picked';
  readonly mode = new FormControl<'range' | 'ev'>('range', { nonNullable: true });
  evConventionEntriesById: Map<string, EvConventionEntry[]> = new Map();
  private lastSelectionChange: OutputFormulaSelectionChange | null = null;

  isCheckingFilenames = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public dataDialog: OutputConfirmProductSearch,
    public dialogRef: MatDialogRef<CreateOutputsComponent>,
    private downloadOutputService: DownloadOutputService,
    private productsService: ProductsService,
    public dialog: MatDialog,
    private alertService: AlertService,
    private outputsCollectionsService: OutputsCollectionsService,
    private destroyRef: DestroyRef,
    @Optional() private filenameConventionService?: FilenameConventionService,
  ) {
    this.postAssetIds = this.dataDialog.postAssetIds ?? [];
  }

  ngOnInit(): void {
    this.loadProducts();
    this.loadEvConventionEntries();
  }

  private get isPostAssetTargeted(): boolean {
    return this.postAssetIds.length > 0;
  }

  loadProducts(): void {
    if (this.isPostAssetTargeted) {
      return;
    }
    this.productsService.listProducts(this.dataDialog?.batchId ?? null).subscribe({
      next: (result: ProductListResult) => {
        this.products = result.products.filter((p) =>
          p.id != null && this.dataDialog.productIds?.includes(p.id),
        );
      },
    });
  }

  onFormulaSelectionChanged(event: OutputFormulaSelectionChange): void {
    if (isDevMode()) {
      console.log('[create-outputs] onFormulaSelectionChanged', {
        formulaCount: event.formulas.length,
        formulasHaveValue: event.formulas.map((f) => ({ id: f.id, hasValue: !!f.value, kind: f.value?.kind })),
        specNamesByIdSize: event.specNamesById?.size ?? 'undef',
        conventionNamesByIdSize: event.conventionNamesById?.size ?? 'undef',
      });
    }
    this.selectedPerMediaSelection = event.perMediaSelection;
    this.selectedFormulaIds = Array.from(
      new Set(event.perMediaSelection.map((entry) => entry.formulaId)),
    );
    this.lastSelectionChange = event;
    this.formulaCoverageSummary = this.computeCoverageSummary(event);
  }

  onModeChanged(next: 'range' | 'ev'): void {
    this.mode.setValue(next);
    this.selectedFormulaIds = [];
    this.selectedPerMediaSelection = [];
    this.lastSelectionChange = null;
    this.formulaCoverageSummary = 'Coverage: no formulas picked';
  }

  get selectedItems(): IProduct[] {
    return this.products;
  }

  get selectedEvIds(): string[] {
    return this.selectedItems
      .map((item) => item.masterSuiteEnhancementVariantId)
      .filter((id): id is string => id != null && id !== '');
  }

  private computeCoverageSummary(event: OutputFormulaSelectionChange): string {
    if (this.mode.value !== 'ev') {
      return event.coverageSummary;
    }
    const hasItems = this.selectedEvIds.length > 0;
    const evFormula = event.formulas.find((formula) => outputFormulaValueIsEvFamily(formula.value));
    const hasFormula = evFormula != null;
    const formulaEvConventionIds = this.extractEvConventionIds(evFormula);
    const coverage = aggregateEvCoverage({
      selectedEvIds: this.selectedEvIds,
      formulaEvConventionIds,
      evConventionEntriesById: this.evConventionEntriesById,
    });
    return describeEvCoverage({
      coveredEvIds: coverage.coveredEvIds,
      missingEvIds: coverage.missingEvIds,
      hasItems,
      hasFormula,
    });
  }

  private extractEvConventionIds(formula: OutputFormula | undefined): string[] {
    if (formula == null) return [];
    const value = formula.value;
    switch (value.kind) {
      case 'evStills':
      case 'evVideos':
      case 'evBoth':
        return value.evConventionIds;
      default:
        return [];
    }
  }

  private loadEvConventionEntries(): void {
    const organizationId = this.dataDialog?.organizationId;
    if (this.filenameConventionService == null || organizationId == null || organizationId === '') {
      return;
    }
    this.filenameConventionService.list(organizationId).subscribe({
      next: (conventions) => {
        this.evConventionEntriesById = new Map(
          conventions
            .filter((convention) => convention.value.payload.mode === 'evBased')
            .map((convention) => {
              const payload = convention.value.payload;
              const entries: EvConventionEntry[] =
                payload.mode === 'evBased'
                  ? payload.entries.map((entry) => ({
                      masterSuiteEnhancementVariantId: entry.masterSuiteEnhancementVariantId,
                    }))
                  : [];
              return [convention.id, entries] as const;
            }),
        );
      },
      error: () => {
        this.evConventionEntriesById = new Map();
      },
    });
  }

  get isCheckFilenamesDisabled(): boolean {
    const hasProducts = (this.dataDialog?.productIds?.length ?? 0) > 0 || this.postAssetIds.length > 0;
    return !hasProducts || this.selectedFormulaIds.length === 0 || this.isCheckingFilenames;
  }

  checkFilenames(): void {
    if (this.isCheckFilenamesDisabled) return;
    this.isCheckingFilenames = true;
    this.outputsCollectionsService
      .previewOutputFilenames({
        productIds: this.dataDialog.productIds ?? [],
        outputFormulaIds: this.selectedFormulaIds,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isCheckingFilenames = false;
          const data: ReviewOutputFilenamesDialogData = { response };
          this.dialog.open(ReviewOutputFilenamesDialogComponent, { data, width: '800px' });
        },
        error: () => {
          this.isCheckingFilenames = false;
          this.alertService.errorToast('Failed to load filename preview.');
        },
      });
  }

  generate(): void {
    const request = this.buildGenerateOutputAssetsRequest();
    this.downloadOutputService
      .generateOutputAssets(request)
      .pipe(
        tap((result: GenerateOutputAssetsResult) => {
          if (result.isValid && result.isSuccess) {
            this.alertService.successToast(
              `Success! ${result.productProcessingCount}/${result.productCount} Selected Products Queued! Output Assets will be ready shortly!`,
            );
          }
        }),
      )
      .subscribe({
        next: () => {
          this.dialogRef.close(DialogResult.Success);
        },
      });
  }

  private buildGenerateOutputAssetsRequest(): GenerateOutputAssetsRequest {
    if (this.mode.value === 'ev') {
      return this.buildEvRequest();
    }
    return this.buildRangeRequest();
  }

  private buildRangeRequest(): RangeGenerateOutputAssetsRequest {
    return {
      mode: 'range',
      productIds: this.dataDialog.productIds ?? [],
      postAssetIds: this.postAssetIds,
      userId: '',
      overwrite: this.overwrite.value,
      formulas: this.buildFormulaReferences(),
      formulaSelections: this.selectedPerMediaSelection.map((entry) => ({
        formulaId: entry.formulaId,
        media: entry.media === 'stills' ? 'Stills' : 'Videos',
      })),
    };
  }

  private buildEvRequest(): EvGenerateOutputAssetsRequest {
    return {
      mode: 'ev',
      productIds: this.dataDialog.productIds ?? [],
      postAssetIds: this.postAssetIds,
      userId: '',
      overwrite: this.overwrite.value,
      formulas: this.buildFormulaReferences(),
    };
  }

  private buildFormulaReferences(): readonly OutputFormulaReference[] {
    const selection = this.lastSelectionChange;
    if (isDevMode()) {
      console.log('[buildFormulaReferences] entry', {
        selectionNull: selection == null,
        formulaCount: selection?.formulas.length ?? 0,
        formulasHaveValue: selection?.formulas.map((f) => ({ id: f.id, hasValue: !!f.value, kind: f.value?.kind })) ?? [],
      });
    }
    if (selection == null) return [];
    const result = buildFormulaReferences(selection.formulas, {
      specNamesById: selection.specNamesById ?? new Map<string, string>(),
      conventionNamesById: selection.conventionNamesById ?? new Map<string, string>(),
    });
    if (isDevMode()) {
      console.log('[buildFormulaReferences] result', {
        resultCount: result.length,
        firstResult: result[0] ? {
          outputFormulaId: result[0].outputFormulaId,
          fileSpecsCount: result[0].fileSpecs.length,
          firstSpec: result[0].fileSpecs[0],
          filenameConvention: result[0].filenameConvention,
        } : null,
      });
    }
    return result;
  }
}
